import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPublishedContentsCount } from '../services/classContentService';
import { getUnreadNotificationCounts } from '../services/notificationService';
import { getAcademicCounts } from '../services/studentService';
import { getAvatarUrl } from '../utils/avatarUtils';

function formatCount(count) {
  if (count > 99) {
    return '۹۹+';
  }

  return new Intl.NumberFormat('fa-IR').format(count);
}

function ActiveCountBadge({ count, size = 'md', label }) {
  if (!count || count < 1) {
    return null;
  }

  const sizeClasses =
    size === 'sm'
      ? 'min-w-[18px] h-[18px] px-1 text-[10px] -top-1.5 -right-1.5'
      : 'min-w-[22px] h-[22px] px-1.5 text-[11px] -top-2 -right-2';

  return (
    <span
      className={`absolute z-10 flex items-center justify-center rounded-full bg-white font-bold text-[#E02727] shadow-[0_2px_8px_rgba(0,0,0,0.18)] ring-2 ring-white/90 ${sizeClasses}`}
      aria-label={label || `${formatCount(count)} مورد فعال`}
    >
      <span
        className="pointer-events-none absolute inset-0 rounded-full bg-[#E02727]/20 animate-ping"
        aria-hidden="true"
      />
      <span className="relative leading-none">{formatCount(count)}</span>
    </span>
  );
}

function HomePage() {
  const { user } = useAuth();
  const [academicCounts, setAcademicCounts] = useState({
    homeworks: 0,
    exams: 0,
    challenges: 0,
    notifications: 0,
    total: 0,
  });
  const [notificationCounts, setNotificationCounts] = useState({
    student: 0,
    parent: 0,
  });
  const [contentsCount, setContentsCount] = useState(0);
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'دانش آموز';
  const profileFields = [
    { label: 'نام:', value: fullName },
    { label: 'شهر:', value: user?.city || 'نامشخص' },
    { label: 'پایه:', value: user?.grade?.title || 'نامشخص' },
  ];

  useEffect(() => {
    let isMounted = true;

    async function loadAcademicCounts() {
      try {
        const counts = await getAcademicCounts();

        if (isMounted) {
          setAcademicCounts({
            homeworks: counts?.homeworks || 0,
            exams: counts?.exams || 0,
            challenges: counts?.challenges || 0,
            notifications: counts?.notifications || 0,
            total: counts?.total || 0,
          });
        }
      } catch {
        // Keep home usable even if counts fail to load.
      }
    }

    async function loadNotificationCounts() {
      try {
        const counts = await getUnreadNotificationCounts();

        if (isMounted) {
          setNotificationCounts({
            student: counts?.student || 0,
            parent: counts?.parent || 0,
          });
        }
      } catch {
        // Keep home usable even if notification counts fail to load.
      }
    }

    async function loadContentsCount() {
      try {
        const count = await getPublishedContentsCount();

        if (isMounted) {
          setContentsCount(count || 0);
        }
      } catch {
        // Keep home usable even if contents count fails to load.
      }
    }

    loadAcademicCounts();
    loadNotificationCounts();
    loadContentsCount();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <div
        className='w-full min-h-[150px] rounded-[10px] p-3 flex flex-col gap-3 sm:gap-0 sm:justify-between'
        style={{ background: 'linear-gradient(20.43deg, rgba(84, 176, 248, 0.7) 0.9%, rgba(145, 73, 133, 0.7) 86.43%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.14)' }}
      >
        <div className="w-full flex items-start gap-2 justify-between">
          <div className='flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1'>
            {profileFields.map(({ label, value }) => (
              <div key={label} className='flex items-center gap-2 min-w-0'>
                <span className='font-light text-[12px] sm:text-[13px] text-white shrink-0'>
                  {label}
                </span>
                <span className='text-[12px] sm:text-[13px] text-white truncate' title={value}>
                  {value}
                </span>
              </div>
            ))}
          </div>

          <div className='shrink-0 w-[64px] h-[64px] sm:w-[90px] sm:h-[90px] rounded-full overflow-hidden border-[3px] border-[#E0E0E0] -mt-1 sm:-mt-[30px]'>
            <img className='object-cover w-full h-full' src={getAvatarUrl(user?.avatar)} alt="" />
          </div>
        </div>

        <div className='w-full flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
          <div className='min-w-0'>
            <p className='text-white text-[14px] sm:text-[15px] font-bold truncate'>
              دبستان مدادرنگی
            </p>
          </div>

          <div className='flex items-center gap-2 shrink-0'>
            <Link
              to="/homeworks"
              className='relative flex-1 sm:flex-none sm:w-[90px] py-1 text-center text-[12px] sm:text-[13px] flex items-center justify-center rounded-[4px] text-white bg-[#00C0B9]'
              aria-label={
                academicCounts.homeworks > 0
                  ? `تکالیف، ${formatCount(academicCounts.homeworks)} مورد فعال`
                  : 'تکالیف'
              }
            >
              <ActiveCountBadge
                count={academicCounts.homeworks}
                size="sm"
                label={`${formatCount(academicCounts.homeworks)} تکلیف فعال`}
              />
              تکالیف
            </Link>

            <Link
              to="/exams"
              className='relative flex-1 sm:flex-none sm:w-[90px] py-1 text-center text-[12px] sm:text-[13px] flex items-center justify-center rounded-[4px] text-white bg-[#00C0B9]'
              aria-label={
                academicCounts.exams > 0
                  ? `آزمون‌ها، ${formatCount(academicCounts.exams)} مورد فعال`
                  : 'آزمون ها'
              }
            >
              <ActiveCountBadge
                count={academicCounts.exams}
                size="sm"
                label={`${formatCount(academicCounts.exams)} آزمون فعال`}
              />
              آزمون ها
            </Link>
          </div>
        </div>
      </div>

      <div className='mt-5'>
        <p className='text-[15px]'>
          امکانات
        </p>

        <div className='grid grid-cols-2 gap-3 w-full mt-4'>
          <Link
            to="/contents"
            className='relative w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #ABCEF5 0%, #67A8EF 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            aria-label={
              contentsCount > 0
                ? `محتوای آموزشی، ${formatCount(contentsCount)} مورد`
                : 'محتوای آموزشی'
            }
          >
            <ActiveCountBadge
              count={contentsCount}
              label={`${formatCount(contentsCount)} محتوای آموزشی`}
            />

            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/content.webp" alt="" />

              <div>
                <p className='text-[15px] text-white'>
                  محتوای آموزشی
                </p>
                {contentsCount > 0 ? (
                  <p className='mt-0.5 text-[11px] text-white/90'>
                    {formatCount(contentsCount)} مورد
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>

          <Link
            to="/homeworks"
            className='relative w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #FFB55A 0%, #FF9238 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            aria-label={
              academicCounts.homeworks > 0
                ? `تکالیف، ${formatCount(academicCounts.homeworks)} مورد فعال`
                : 'تکالیف'
            }
          >
            <ActiveCountBadge
              count={academicCounts.homeworks}
              label={`${formatCount(academicCounts.homeworks)} تکلیف فعال`}
            />

            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/homework.webp" alt="" />

              <div>
                <p className='text-[15px] text-white'>
                  تکالیف
                </p>
                {academicCounts.homeworks > 0 ? (
                  <p className='mt-0.5 text-[11px] text-white/90'>
                    {formatCount(academicCounts.homeworks)} مورد فعال
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>

          <Link
            to="/challenges"
            className='relative w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #B9ABFF 0%, #896CFF 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            aria-label={
              academicCounts.challenges > 0
                ? `چالش‌ها، ${formatCount(academicCounts.challenges)} مورد فعال`
                : 'چالش ها'
            }
          >
            <ActiveCountBadge
              count={academicCounts.challenges}
              label={`${formatCount(academicCounts.challenges)} چالش فعال`}
            />

            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/challange.webp" alt="" />

              <div>
                <p className='text-[15px] text-white'>
                  چالش ها
                </p>
                {academicCounts.challenges > 0 ? (
                  <p className='mt-0.5 text-[11px] text-white/90'>
                    {formatCount(academicCounts.challenges)} مورد فعال
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>

          <Link
            to="/parents"
            className='relative w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #7FDE86 0%, #1FC24E 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            aria-label={
              notificationCounts.parent > 0
                ? `پنجره اولیا، ${formatCount(notificationCounts.parent)} اطلاعیه خوانده‌نشده`
                : 'پنجره اولیا'
            }
          >
            <ActiveCountBadge
              count={notificationCounts.parent}
              label={`${formatCount(notificationCounts.parent)} اطلاعیه خوانده‌نشده اولیا`}
            />

            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/parent.webp" alt="" />

              <div>
                <p className='text-[15px] text-white'>
                  پنجره اولیا
                </p>
                {notificationCounts.parent > 0 ? (
                  <p className='mt-0.5 text-[11px] text-white/90'>
                    {formatCount(notificationCounts.parent)} اطلاعیه جدید
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>

          <Link
            to="/calendar"
            className='w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #EB8181 0%, #E02727 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
          >
            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/calender.webp" alt="" />

              <p className='text-[15px] text-white'>
                برنامه هفتگی
              </p>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>

          <Link
            to="/notifications"
            className='relative w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #75E3F7 0%, #00C7EA 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            aria-label={
              academicCounts.notifications > 0
                ? `اطلاعیه‌ها، ${formatCount(academicCounts.notifications)} اطلاعیه خوانده‌نشده`
                : 'اطلاعیه‌ها'
            }
          >
            <ActiveCountBadge
              count={academicCounts.notifications}
              label={`${formatCount(academicCounts.notifications)} اطلاعیه خوانده‌نشده`}
            />

            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/10.png" alt="" />

              <div>
                <p className='text-[15px] text-white'>
                  اطلاعیه‌ها
                </p>
                {academicCounts.notifications > 0 ? (
                  <p className='mt-0.5 text-[11px] text-white/90'>
                    {formatCount(academicCounts.notifications)} اطلاعیه جدید
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>

          <Link
            to="/exams"
            className='relative w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #7FDE86 0%, #1FC24E 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
            aria-label={
              academicCounts.exams > 0
                ? `آزمون آنلاین، ${formatCount(academicCounts.exams)} مورد فعال`
                : 'آزمون آنلاین'
            }
          >
            <ActiveCountBadge
              count={academicCounts.exams}
              label={`${formatCount(academicCounts.exams)} آزمون فعال`}
            />

            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/exam.webp" alt="" />

              <div>
                <p className='text-[15px] text-white'>
                  آزمون آنلاین
                </p>
                {academicCounts.exams > 0 ? (
                  <p className='mt-0.5 text-[11px] text-white/90'>
                    {formatCount(academicCounts.exams)} مورد فعال
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>

          <Link
            to="/grades"
            className='w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #A7D2FB 0%, #49A8F4 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
          >
            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/grade.webp" alt="" />

              <p className='text-[15px] text-white'>
                کارنامه
              </p>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>
        </div>

        <div className='grid grid-cols-2 gap-3 w-full mt-3'>
          <div
            className='w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #ABB5FF 0%, #4097D9 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
          >
            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src='/images/class.webp' alt="" />

              <p className='text-[15px] text-white'>
                کلاس آنلاین
              </p>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </div>

          <Link
            to="/classes"
            className='w-full flex items-center justify-between p-2 rounded-[10px]'
            style={{ background: 'linear-gradient(90deg, #7EE8DB 0%, #14B8A6 100%)', boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)' }}
          >
            <div
              className='flex flex-col gap-2 justify-between items-start h-full'
            >
              <img className='h-[50px]' src="/images/program.webp" alt="" />

              <p className='text-[15px] text-white'>
                برنامه کلاسی
              </p>
            </div>

            <div>
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g opacity="0.6">
                  <path d="M20.083 12.5834L15.5649 16.7787C14.8548 17.4381 14.8548 18.562 15.5649 19.2214L20.083 23.4167M17.9163 1.25004C8.7116 1.25004 1.24968 8.71197 1.24968 17.9167C1.24968 27.1215 8.7116 34.5834 17.9163 34.5834C27.1211 34.5834 34.583 27.1215 34.583 17.9167C34.583 8.71196 27.1211 1.25004 17.9163 1.25004Z" stroke="white" stroke-width="2.5" stroke-linecap="round" />
                </g>
              </svg>
            </div>
          </Link>
        </div>
      </div>
    </>
  );
}

export default HomePage;
